import {PlatformService} from './presentation/services/platform.service';
import {NgModule} from '@angular/core';
import {BrowserModule, provideClientHydration} from "@angular/platform-browser";
import {TranslateLoader, TranslateModule, TranslateService} from "@ngx-translate/core";
import {TranslateHttpLoader} from "@ngx-translate/http-loader";
import {HttpClient, HttpClientModule, provideHttpClient, withFetch} from "@angular/common/http";
import {AppRoutingModule, routes} from "./app-routing.module";
import {AppComponent} from "./app.component";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {repositoryProviders} from "./di/providers";
import {MatSnackBarModule} from "@angular/material/snack-bar";
import {MatDialogModule} from "@angular/material/dialog";
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatBottomSheetModule} from "@angular/material/bottom-sheet";
import {DelayService} from "@app/delay";
import {AngularSvgIconModule} from "angular-svg-icon";
import {DialogsModule} from "@app2u/dialogs";
import {IonicModule, IonicRouteStrategy} from '@ionic/angular';
import {ProductSearchBarModule} from "@app/presentation/components/product-search-bar/product-search-bar.module";
import {AppVersion} from "@awesome-cordova-plugins/app-version/ngx";
import {SelectorFieldModule} from './presentation/components/selector-field/selector-field.module';
import {NoStockServiceModule} from './presentation/services/no-stock/no-stock-service.module';
import {
  LocalizeParser,
  LocalizeRouterModule,
  LocalizeRouterSettings,
  ManualParserLoader
} from '@gilsdav/ngx-translate-router';
import {Location} from "@angular/common";
import {PortalModule} from "@angular/cdk/portal";
import {LoginModule} from "@app/presentation/pages/login/login.module";
import {SignUpModule} from "@app/presentation/pages/sign-up/sign-up.module";
import {ResetPasswordModule} from "@app/presentation/pages/reset-password/reset-password.module";
import {AccountModule} from "@app/presentation/pages/account/account.module";
import {ContactModule} from "@app/presentation/pages/contact/contact.module";
import {CartDetailModule} from "@app/presentation/pages/cart-detail/cart-detail.module";
import {ChangeDeliveryPointModule} from "@app/presentation/pages/change-delivery-point/change-delivery-point.module";
import {CreateDeliveryPointModule} from "@app/presentation/pages/create-delivery-point/create-delivery-point.module";
import {SelectDeliveryPointModule} from "@app/presentation/pages/select-delivery-point/select-delivery-point.module";
import {CreateListModule} from "@app/presentation/pages/create-list/create-list.module";
import {GoogleTagManagerModule} from "angular-google-tag-manager";
import {environment} from "src/environments/environment";
import {SeoModule} from "@app/presentation/services/seo/seo.module";
import {
  DisplayOnlyOnBrowserModule
} from "@app/presentation/directives/display-only-on-browser/display-only-on-browser.module";
import {Observable, of} from 'rxjs';
import {readFileSync} from 'fs';
import {join} from 'path';
import packageJson from 'package.json';
import {RouteReuseStrategy} from "@angular/router";
import {LoadingServiceModule} from "@app/presentation/services/loading/loading-service.module";
import {RecaptchaModule} from "@app/presentation/services/recaptcha/recaptcha.module";
import {LazyLoadDirectiveModule} from "@app/presentation/directives/lazy-load-image/lazy-load.module";
import {ToastModule} from "@app/presentation/services/toast/toast.module";
import {ErrorModalComponent} from '@app/presentation/services/error-modal/error-modal.component';
import {ErrorModalService} from '@app/presentation/services/error-modal/error-modal.service';
import {ModalModule} from "@app/presentation/services/modal/modal.module";
import {DeleteListModule} from "@app/presentation/pages/delete-list/delete-list.module";
import {ConfirmationModalModule} from "@app/presentation/services/confirmation-modal/confirmation-modal.module";

// Capacitor.getPlatform() values for mobile
export const PLATFORM_IOS = 'ios';
export const PLATFORM_ANDROID = 'android';

export class TranslateFSLoader implements TranslateLoader {
  constructor(private prefix = 'i18n', private suffix = '.json') { }
  public getTranslation(lang: string): Observable<any> {
    const path = join(__dirname, '../browser/assets/', this.prefix, `${lang}${this.suffix}`);
    const data = JSON.parse(readFileSync(path, 'utf8'));
    return of(data);
  }
}

export function HttpLoaderFactory(http: HttpClient, platformService: PlatformService): TranslateLoader {
  if (platformService.isServer) {
    return new TranslateFSLoader();
  }
  return new TranslateHttpLoader(http, `/assets/i18n/`, ".json?v=" + packageJson.version);
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    GoogleTagManagerModule.forRoot({
      id: environment.GTM_ID,
    }),
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    TranslateModule.forRoot({
      defaultLanguage: "es",
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient, PlatformService],
      },
    }),
    LocalizeRouterModule.forRoot(routes, {
      parser: {
        provide: LocalizeParser,
        useFactory: (translate: TranslateService, location: Location, settings: LocalizeRouterSettings) =>
          new ManualParserLoader(
            translate,
            location,
            settings,
            ['es', 'en', 'fr', 'pt'],
            'ROUTES.',
            '!'
          ),
        deps: [TranslateService, Location, LocalizeRouterSettings, HttpClient],
      },
      initialNavigation: true,
      defaultLangFunction: (languages, cachedLang, browserLang) => browserLang ?? languages[0],
    }),
    BrowserAnimationsModule,
    MatSnackBarModule,
    MatDialogModule,
    MatSidenavModule,
    MatBottomSheetModule,
    AngularSvgIconModule.forRoot(),
    DialogsModule,
    IonicModule.forRoot({ mode: 'md' }),
    ProductSearchBarModule,
    ErrorModalComponent,
    SelectorFieldModule,
    NoStockServiceModule,
    PortalModule,
    ModalModule,
    ConfirmationModalModule,
    //SideSheet Pages
    LoginModule,
    SignUpModule,
    ResetPasswordModule,
    SelectDeliveryPointModule,
    AccountModule,
    ContactModule,
    CartDetailModule,
    ChangeDeliveryPointModule,
    CreateDeliveryPointModule,
    CreateListModule,
    DeleteListModule,
    SeoModule,
    DisplayOnlyOnBrowserModule,
    LoadingServiceModule,
    RecaptchaModule,
    LazyLoadDirectiveModule,
    ToastModule,
  ],
  providers: [
    DelayService,
    Location,
    ErrorModalService,
    provideClientHydration(),
    provideHttpClient(withFetch()),
    repositoryProviders,
    AppVersion,
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy,
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
